import React, { useRef, useEffect } from "react";
import useFetch from "../../utils/useFetch";
import convertToQueryParams from "../../utils/convertToQueryParams";
import useFilters from "../../hooks/useFilters";
import Card from "../card/Card";

function AllProducts() {
  const Api = `inventory/products/`;
  const buttonRef = useRef(null);
  const [products, setProducts] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const {
    filters,
    setFilters,
  } = useFilters({
    is_active: true,
    page: 1,
    size: 15,
    search: null,
    tags: null,
  });

  const { data, loading, error } = useFetch(`${Api}${convertToQueryParams(filters)}`);

  useEffect(() => {
    if (data) {
      if (filters.page === 1) {
        setProducts(data.results || []);
      } else {
        setProducts(prev => [...prev, ...(data.results || [])]);
      }
      setHasMore(!!data.next);
      setIsLoadingMore(false);
    }
  }, [data, filters.page]);

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore && !loading) {
      setIsLoadingMore(true);
      setFilters(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };

  // Set up intersection observer for auto-loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !isLoadingMore) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.5 // Trigger when button is 50% visible
      }
    );

    const currentButton = buttonRef.current;
    if (currentButton) {
      observer.observe(currentButton);
    }

    return () => {
      if (currentButton) {
        observer.unobserve(currentButton);
      }
    };
  }, [hasMore, loading, isLoadingMore]);

  return (
    <div className="lg:py-16 py-10 mx-auto max-w-screen-2xl px-3 sm:px-10">
      <div className="mb-10 flex justify-between items-center">
        <div className="text-left">
          <h2 className="text-xl lg:text-3xl mb-2 text-black font-semibold">
            All Products
          </h2>
          <p className="text-base text-gray-600 leading-6">
            Browse through our complete collection of products
          </p>
        </div>
      </div>

      {loading && products.length === 0 ? (
        <div className="flex">
          <div className="w-full">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-4 md:gap-8 lg:gap-8">
              {[...Array(15)].map((_, index) => (
                <div key={index} className="group box-border overflow-hidden flex rounded-md shadow-sm pr-0 flex-col items-center bg-white relative">
                  <div className="relative flex justify-center w-full h-60 bg-gray-100 animate-pulse">
                    <div className="absolute top-4 right-4 z-10">
                      <div className="p-2 bg-gray-300 rounded-full h-8 w-8"></div>
                    </div>
                  </div>
                  <div className="w-full px-3 lg:px-4 pb-4 overflow-hidden">
                    <div className="relative mb-1 mt-4 space-y-2">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-1/2"></div>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-1/3"></div>
                      <div className="h-8 bg-gray-200 animate-pulse rounded-md w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="text-center text-red-500">Error: {error}</div>
      ) : products.length === 0 ? (
        <div className="text-center text-gray-500">No products found</div>
      ) : (
        <>
          <div className="flex">
            <div className="w-full">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-4 md:gap-8 lg:gap-8">
                {products.map((product, index) => (
                  <Card key={product.id || index} data={product} />
                ))}
              </div>
            </div>
          </div>

          {/* Load More Button */}
          <div ref={buttonRef} className="flex justify-center mt-8">
            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore || loading}
                className={`px-6 py-3 rounded-md text-white font-medium transition-all duration-200 ${
                  isLoadingMore || loading
                    ? 'bg-brand-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {isLoadingMore || loading ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                ) : (
                  'Load More'
                )}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AllProducts; 